import { useState, useCallback } from 'react'
import { tokenResolutionApi } from '@/services/api/tokenResolution'
import type {
  QualificationMatrixStatus,
  TokenResolutionContext,
  ResolutionType,
} from '@/types/tokenResolution'

interface ResolutionResult {
  token: string
  resolutionType: ResolutionType
  success: boolean
  data: Record<string, unknown>
}

interface UseTokenResolutionWorkflowState {
  qualificationMatrixStatus: QualificationMatrixStatus | null
  pendingResolutions: Map<string, TokenResolutionContext>
  resolutionResults: ResolutionResult[]
  loading: boolean
  error: string | null
}

interface UseTokenResolutionWorkflowReturn extends UseTokenResolutionWorkflowState {
  checkQualificationMatrix: () => Promise<void>
  addResolution: (token: string, context: TokenResolutionContext) => void
  removeResolution: (token: string) => void
  getPendingResolutions: () => TokenResolutionContext[]
  submitAllResolutions: (sessionId: string) => Promise<void>
  reset: () => void
}

export function useTokenResolutionWorkflow(): UseTokenResolutionWorkflowReturn {
  const [state, setState] = useState<UseTokenResolutionWorkflowState>({
    qualificationMatrixStatus: null,
    pendingResolutions: new Map(),
    resolutionResults: [],
    loading: false,
    error: null,
  })

  const checkQualificationMatrix = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const status = await tokenResolutionApi.getQualificationMatrixStatus()
      setState(prev => ({
        ...prev,
        qualificationMatrixStatus: status,
        loading: false,
      }))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check qualification matrix'
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }))
    }
  }, [])

  const addResolution = useCallback((token: string, context: TokenResolutionContext) => {
    setState(prev => {
      const newMap = new Map(prev.pendingResolutions)
      newMap.set(token, context)
      return { ...prev, pendingResolutions: newMap }
    })
  }, [])

  const removeResolution = useCallback((token: string) => {
    setState(prev => {
      const newMap = new Map(prev.pendingResolutions)
      newMap.delete(token)
      return { ...prev, pendingResolutions: newMap }
    })
  }, [])

  const getPendingResolutions = useCallback(() => {
    return Array.from(state.pendingResolutions.values())
  }, [state.pendingResolutions])

  const submitAllResolutions = useCallback(async (sessionId: string) => {
    const resolutions = Array.from(state.pendingResolutions.values())
    if (resolutions.length === 0) return

    setState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const results: ResolutionResult[] = []

      for (const resolution of resolutions) {
        let success = false
        const data: Record<string, unknown> = {}

        if (resolution.resolution_type === 'alias') {
          if (resolution.resolution_data.canonical_subject_id) {
            await tokenResolutionApi.createSubjectAlias({
              alias_name: resolution.token,
              canonical_subject_id: resolution.resolution_data.canonical_subject_id as string,
            })
            success = true
            data.alias_type = 'subject'
          } else if (resolution.resolution_data.canonical_background_id) {
            await tokenResolutionApi.createBackgroundAlias({
              alias_name: resolution.token,
              canonical_background_id: resolution.resolution_data.canonical_background_id as string,
            })
            success = true
            data.alias_type = 'background'
          }
        } else if (resolution.resolution_type === 'new_subject') {
          await tokenResolutionApi.createSubjectWithBackground({
            subject_name: resolution.resolution_data.subject_name as string,
            background_id: resolution.resolution_data.background_id as string | undefined,
            group_name: resolution.resolution_data.group_name as string | undefined,
          }, sessionId)
          success = true
          data.subject_name = resolution.resolution_data.subject_name
        } else if (resolution.resolution_type === 'new_background') {
          await tokenResolutionApi.createBackground({
            name: resolution.resolution_data.background_name as string,
          }, sessionId)
          success = true
          data.background_name = resolution.resolution_data.background_name
        } else if (resolution.resolution_type === 'subject_with_new_background') {
          await tokenResolutionApi.createSubjectWithBackground({
            subject_name: resolution.resolution_data.subject_name as string,
            create_new_background: true,
            new_background_name: resolution.resolution_data.new_background_name as string,
            group_name: resolution.resolution_data.group_name as string | undefined,
          }, sessionId)
          success = true
          data.subject_name = resolution.resolution_data.subject_name
          data.new_background_name = resolution.resolution_data.new_background_name
        }

        results.push({
          token: resolution.token,
          resolutionType: resolution.resolution_type,
          success,
          data,
        })
      }

      setState(prev => ({
        ...prev,
        pendingResolutions: new Map(),
        resolutionResults: results,
        loading: false,
      }))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit resolutions'
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }))
    }
  }, [state.pendingResolutions])

  const reset = useCallback(() => {
    setState({
      qualificationMatrixStatus: null,
      pendingResolutions: new Map(),
      resolutionResults: [],
      loading: false,
      error: null,
    })
  }, [])

  return {
    ...state,
    checkQualificationMatrix,
    addResolution,
    removeResolution,
    getPendingResolutions,
    submitAllResolutions,
    reset,
  }
}